import { Form } from "antd";
import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  flattenExternalInstitutionAccounts,
  renderAccountOptionLabel,
} from "../utils/accountTransactionModalHelper";
import { formatAccountText } from "../../../utils/formatUtils";
import {
  buildInitialValues,
  buildSubmitPayload,
  resetRateFields,
} from "../utils/accountTransactionModalForm";
import { applyRateModeToValues, getRateMode } from "../utils/rateMode";

export default function useAccountTransactionModal({
  open,
  mode,
  form,
  selectedAccount,
  institutionAccounts,
  manualSenderAccounts,
  externalInstitutions,
  onTransferScopeChange,
  onSubmit,
}) {
  const isManual = mode === "MANUAL";
  const isTransfer = mode === "TRANSFER";

  const senderBankAccountId = Form.useWatch("senderBankAccountId", form);
  const transferScope = Form.useWatch("transferScope", form);
  const externalInstitutionId = Form.useWatch("externalInstitutionId", form);
  const receiverCurrencyFilter = Form.useWatch("receiverCurrencyFilter", form);
  const receiverBankAccountId = Form.useWatch("receiverBankAccountId", form);
  const description = Form.useWatch("description", form);
  const amount = Form.useWatch("amount", form);
  const fee = Form.useWatch("fee", form);
  const sharedRate = Form.useWatch("sharedRate", form);
  const senderRate = Form.useWatch("senderRate", form);
  const receiverRate = Form.useWatch("receiverRate", form);
  const transactionDate = Form.useWatch("transactionDate", form);

  const externalAccounts = useMemo(() => {
    return flattenExternalInstitutionAccounts(externalInstitutions || []);
  }, [externalInstitutions]);

  const externalInstitutionOptions = useMemo(() => {
    return (externalInstitutions || []).map((item) => ({
      value: item.institution?.id,
      label: item.institution?.name || "-",
    }));
  }, [externalInstitutions]);

  const availableManualSenderAccounts = useMemo(() => {
    if (!isManual) return [];

    const receiverCurrency = selectedAccount?.currency;

    return (manualSenderAccounts || []).filter((account) => {
      if (!receiverCurrency) return true;
      return account.currency === receiverCurrency;
    });
  }, [isManual, manualSenderAccounts, selectedAccount]);

  const senderOptions = useMemo(() => {
    if (!isManual) return [];

    return availableManualSenderAccounts.map((account) => ({
      value: account.id,
      label: renderAccountOptionLabel(account, "INTERNAL"),
      searchText: formatAccountText(account),
    }));
  }, [isManual, availableManualSenderAccounts]);

  const manualSenderAccount = useMemo(() => {
    if (!isManual) return null;

    return (
      availableManualSenderAccounts.find(
        (account) => String(account.id) === String(senderBankAccountId)
      ) || null
    );
  }, [isManual, availableManualSenderAccounts, senderBankAccountId]);

  const receiverAccountsSource = useMemo(() => {
    if (!isTransfer) return [];

    if (transferScope === "EXTERNAL") {
      let accounts = externalAccounts;

      if (externalInstitutionId) {
        accounts = accounts.filter(
          (account) =>
            String(account.institutionId) === String(externalInstitutionId)
        );
      }

      return accounts;
    }

    return institutionAccounts || [];
  }, [
    isTransfer,
    transferScope,
    externalAccounts,
    externalInstitutionId,
    institutionAccounts,
  ]);

  const receiverCurrencyOptions = useMemo(() => {
    if (!isTransfer) return [];

    const currencies = [
      ...new Set(
        receiverAccountsSource.map((account) => account.currency).filter(Boolean)
      ),
    ];

    return [
      { value: "ALL", label: "Бүх валют" },
      ...currencies.map((currency) => ({
        value: currency,
        label: currency,
      })),
    ];
  }, [isTransfer, receiverAccountsSource]);

  const filteredReceiverOptions = useMemo(() => {
    if (!isTransfer) return [];

    return receiverAccountsSource
      .filter((account) => {
        if (!selectedAccount?.id) return true;

        if (transferScope === "INTERNAL" && account.id === selectedAccount.id) {
          return false;
        }

        if (!receiverCurrencyFilter || receiverCurrencyFilter === "ALL") {
          return true;
        }

        return account.currency === receiverCurrencyFilter;
      })
      .map((account) => ({
        value: account.id,
        label: renderAccountOptionLabel(account, transferScope),
        searchText: [
          account.bank || "",
          account.accountNumber || "",
          account.currency || "",
          account.institutionName || "",
        ]
          .join(" ")
          .toLowerCase(),
        institutionId: account.institutionId,
        institutionName: account.institutionName,
      }));
  }, [
    isTransfer,
    receiverAccountsSource,
    selectedAccount,
    transferScope,
    receiverCurrencyFilter,
  ]);

  const handleReceiverAccountSelect = (value, option) => {
    if (transferScope !== "EXTERNAL") return;

    const institutionId = option?.institutionId;
    if (!institutionId) return;

    form.setFieldsValue({
      externalInstitutionId: institutionId,
    });
  };

  const receiverAccount = useMemo(() => {
    if (isManual) {
      return selectedAccount || null;
    }

    if (!isTransfer) return null;

    return (
      receiverAccountsSource.find(
        (account) => String(account.id) === String(receiverBankAccountId)
      ) || null
    );
  }, [
    isManual,
    isTransfer,
    selectedAccount,
    receiverAccountsSource,
    receiverBankAccountId,
  ]);

  const senderCurrency = isManual
    ? manualSenderAccount?.currency
    : selectedAccount?.currency;

  const receiverCurrency = isManual
    ? selectedAccount?.currency
    : receiverAccount?.currency;

  const rateMode = useMemo(() => {
    return getRateMode({
      isManual,
      senderCurrency,
      receiverCurrency,
    });
  }, [isManual, senderCurrency, receiverCurrency]);

  const numericAmount = Number(amount || 0);
  const numericFee = Number(fee || 0);
  const senderWalletBalance = Number(selectedAccount?.walletBalance || 0);

  const isAmountExceedingWallet =
    isTransfer &&
    numericAmount + numericFee > 0 &&
    numericAmount + numericFee > senderWalletBalance;

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue(
      buildInitialValues({
        isManual,
        isTransfer,
        selectedAccount,
        now: dayjs(),
      })
    );
  }, [open, isManual, isTransfer, selectedAccount, form]);

  useEffect(() => {
    if (!open || !isTransfer) return;

    const receiverStillValid = receiverAccountsSource.some((account) => {
      if (String(account.id) !== String(receiverBankAccountId)) return false;

      if (transferScope === "INTERNAL" && account.id === selectedAccount?.id) {
        return false;
      }

      if (!receiverCurrencyFilter || receiverCurrencyFilter === "ALL") {
        return true;
      }

      return account.currency === receiverCurrencyFilter;
    });

    if (!receiverStillValid) {
      form.setFieldsValue(resetRateFields());
    }
  }, [
    open,
    isTransfer,
    receiverAccountsSource,
    receiverBankAccountId,
    receiverCurrencyFilter,
    transferScope,
    selectedAccount,
    form,
  ]);

  useEffect(() => {
    if (!open) return;

    const currentValues = form.getFieldsValue([
      "sharedRate",
      "senderRate",
      "receiverRate",
    ]);

    const nextValues = applyRateModeToValues(rateMode, currentValues);
    form.setFieldsValue(nextValues);
  }, [open, rateMode, form]);

  useEffect(() => {
    if (!open) return;

    if (
      rateMode.sharedInput &&
      sharedRate !== undefined &&
      sharedRate !== null &&
      sharedRate !== ""
    ) {
      form.setFieldsValue({
        senderRate: sharedRate,
        receiverRate: sharedRate,
      });
    }
  }, [open, rateMode.sharedInput, sharedRate, form]);

  const handleTransferScopeSelect = async (value) => {
    form.setFieldsValue({
      externalInstitutionId: undefined,
      receiverCurrencyFilter: "ALL",
      receiverBankAccountId: undefined,
      ...resetRateFields(),
    });

    if (onTransferScopeChange) {
      await onTransferScopeChange(value);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (isTransfer && Number(values.amount || 0) > senderWalletBalance) {
        return;
      }

      const normalizedValues = applyRateModeToValues(rateMode, values);

      await onSubmit(
        buildSubmitPayload({
          values: normalizedValues,
          rateMode,
          transactionDateFormat: "YYYY-MM-DDTHH:mm:ss",
        })
      );
    } catch (err) {
      console.error("Modal validation failed", err);
    }
  };

  return {
    isManual,
    isTransfer,

    transferScope,
    externalInstitutionId,
    receiverCurrencyFilter,
    receiverBankAccountId,

    amount,
    description,
    sharedRate,

    senderOptions,
    externalInstitutionOptions,
    receiverCurrencyOptions,
    filteredReceiverOptions,
    handleReceiverAccountSelect,

    receiverAccount,
    senderCurrency,
    receiverCurrency,
    rateMode,
    transactionDate,
    senderRate,
    receiverRate,
    fee,

    senderWalletBalance,
    isAmountExceedingWallet,

    handleTransferScopeSelect,
    handleOk,
  };
}